
import { useState } from 'react';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

const DocsPage = () => {
  const [activeSection, setActiveSection] = useState('getting-started');

  const sections = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: 'ri-rocket-line',
      items: [
        'Quick Start Guide',
        'Account Setup',
        'First AI Tool Integration',
        'Dashboard Overview'
      ]
    },
    {
      id: 'api-reference',
      title: 'API Reference',
      icon: 'ri-code-line',
      items: [
        'Authentication',
        'Lead Generation API',
        'Staging API',
        'Analytics API',
        'Webhooks'
      ]
    },
    {
      id: 'integrations',
      title: 'Integrations',
      icon: 'ri-plug-line',
      items: [
        'CRM Integration',
        'MLS Connection',
        'Social Media Platforms',
        'Email Marketing Tools'
      ]
    },
    {
      id: 'ai-tools',
      title: 'AI Tools',
      icon: 'ri-brain-line',
      items: [
        'AI Staging Setup',
        'Chatbot Configuration',
        'Lead Scoring',
        'Market Analytics'
      ]
    },
    {
      id: 'troubleshooting',
      title: 'Troubleshooting',
      icon: 'ri-tools-line',
      items: [
        'Common Issues',
        'Error Codes',
        'Performance Optimization',
        'Support Contact'
      ]
    }
  ];

  const content = {
    'getting-started': {
      title: 'Getting Started with AI Real Estate Tools',
      content: `
        <h2>Welcome to the Future of Real Estate</h2>
        <p>Our AI-powered platform is designed to revolutionize how real estate professionals work. This guide will help you get started quickly and efficiently.</p>
        
        <h3>Step 1: Account Setup</h3>
        <p>After signing up, you'll receive a welcome email with your login credentials. Your first login will trigger the setup wizard.</p>
        
        <h3>Step 2: Connect Your Tools</h3>
        <p>Integrate with your existing CRM, MLS, and marketing tools for seamless workflow automation.</p>
        
        <h3>Step 3: Configure AI Preferences</h3>
        <p>Set up your AI preferences for lead scoring, market analysis, and automated responses.</p>
        
        <h3>Step 4: Start Using AI Tools</h3>
        <p>Begin with our most popular features: AI staging and lead generation.</p>
      `
    },
    'api-reference': {
      title: 'API Reference Documentation',
      content: `
        <h2>RESTful API Overview</h2>
        <p>Our API follows REST principles and returns JSON responses. All requests require authentication.</p>
        
        <h3>Authentication</h3>
        <pre><code>
Authorization: Bearer YOUR_API_KEY
Content-Type: application/json
        </code></pre>
        
        <h3>Lead Generation Endpoint</h3>
        <pre><code>
POST /api/v1/leads/generate
{
  "criteria": {
    "location": "string",
    "price_range": [min, max],
    "property_type": "string"
  }
}
        </code></pre>
        
        <h3>AI Staging Endpoint</h3>
        <pre><code>
POST /api/v1/staging/generate
{
  "property_id": "string",
  "room_type": "string",
  "style_preference": "string"
}
        </code></pre>
      `
    },
    'integrations': {
      title: 'Third-Party Integrations',
      content: `
        <h2>Supported Integrations</h2>
        <p>Connect your favorite tools to create a seamless workflow.</p>
        
        <h3>CRM Systems</h3>
        <ul>
          <li>Salesforce</li>
          <li>HubSpot</li>
          <li>Pipedrive</li>
          <li>Chime</li>
        </ul>
        
        <h3>MLS Platforms</h3>
        <ul>
          <li>MLS Grid</li>
          <li>Bright MLS</li>
          <li>CRMLS</li>
          <li>NWMLS</li>
        </ul>
        
        <h3>Marketing Tools</h3>
        <ul>
          <li>Mailchimp</li>
          <li>Constant Contact</li>
          <li>Facebook Ads</li>
          <li>Google Ads</li>
        </ul>
      `
    },
    'ai-tools': {
      title: 'AI Tools Configuration',
      content: `
        <h2>AI Tool Setup Guide</h2>
        <p>Learn how to configure and optimize each AI tool for maximum effectiveness.</p>
        
        <h3>AI Staging Configuration</h3>
        <p>Upload property photos and select staging preferences. Our AI will generate furnished versions automatically.</p>
        
        <h3>Chatbot Setup</h3>
        <p>Train your AI chatbot with your specific responses and integrate it with your website.</p>
        
        <h3>Lead Scoring Algorithm</h3>
        <p>Customize the lead scoring parameters based on your market and preferences.</p>
        
        <h3>Market Analytics Dashboard</h3>
        <p>Set up automated reports and alerts for market changes in your area.</p>
      `
    },
    'troubleshooting': {
      title: 'Troubleshooting Guide',
      content: `
        <h2>Common Issues and Solutions</h2>
        <p>Find quick solutions to the most common problems.</p>
        
        <h3>API Connection Issues</h3>
        <p>Check your API key and ensure it's properly formatted in the request headers.</p>
        
        <h3>Staging Quality Issues</h3>
        <p>Ensure uploaded images are high resolution (minimum 1920x1080) and well-lit.</p>
        
        <h3>Integration Problems</h3>
        <p>Verify that your third-party service credentials are current and have proper permissions.</p>
        
        <h3>Performance Optimization</h3>
        <p>Use webhook endpoints for real-time updates instead of polling APIs frequently.</p>
      `
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-600 to-blue-800 text-white py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl font-bold mb-6">
              Documentation Center
            </h1>
            <p className="text-xl mb-8 text-blue-100">
              Complete guides, API references, and technical documentation for our AI platform
            </p>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 max-w-md mx-auto">
              <div className="flex items-center justify-center space-x-4">
                <i className="ri-search-line text-2xl"></i>
                <input 
                  type="text" 
                  placeholder="Search documentation..."
                  className="bg-transparent border-none outline-none text-white placeholder-blue-200 flex-1"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Documentation Content */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-4 gap-8">
            {/* Sidebar Navigation */}
            <div className="lg:col-span-1">
              <div className="bg-gray-50 rounded-xl p-6 sticky top-6">
                <h3 className="text-lg font-bold mb-6 text-gray-800">Documentation</h3>
                <nav className="space-y-2">
                  {sections.map((section) => (
                    <div key={section.id}>
                      <button
                        onClick={() => setActiveSection(section.id)}
                        className={`w-full text-left p-3 rounded-lg transition-colors cursor-pointer ${
                          activeSection === section.id
                            ? 'bg-blue-600 text-white'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        <div className="flex items-center space-x-3">
                          <i className={`${section.icon} text-lg`}></i>
                          <span className="font-medium">{section.title}</span>
                        </div>
                      </button>
                      {activeSection === section.id && (
                        <div className="ml-6 mt-2 space-y-1">
                          {section.items.map((item, index) => (
                            <button
                              key={index}
                              className="block w-full text-left p-2 text-sm text-gray-600 hover:text-blue-600 transition-colors cursor-pointer"
                            >
                              {item}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </nav>
              </div>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-xl shadow-lg p-8">
                <h1 className="text-3xl font-bold mb-6 text-gray-800">
                  {content[activeSection]?.title}
                </h1>
                <div 
                  className="prose prose-lg max-w-none"
                  dangerouslySetInnerHTML={{ __html: content[activeSection]?.content }}
                />
                
                {/* Code Examples */}
                {activeSection === 'api-reference' && (
                  <div className="mt-8 space-y-6">
                    <div className="bg-gray-900 rounded-lg p-6 text-white">
                      <h4 className="text-lg font-bold mb-4 text-green-400">Example Response</h4>
                      <pre className="text-sm overflow-x-auto">
{`{
  "success": true,
  "data": {
    "leads": [
      {
        "id": "lead_123",
        "name": "John Smith",
        "email": "john@example.com",
        "phone": "+1234567890",
        "score": 85,
        "property_interest": "luxury_homes"
      }
    ],
    "total": 1,
    "page": 1
  }
}`}
                      </pre>
                    </div>
                  </div>
                )}

                {/* Navigation */}
                <div className="flex justify-between items-center mt-12 pt-8 border-t border-gray-200">
                  <button className="flex items-center space-x-2 text-blue-600 hover:text-blue-700 cursor-pointer">
                    <i className="ri-arrow-left-line"></i>
                    <span>Previous</span>
                  </button>
                  <button className="flex items-center space-x-2 text-blue-600 hover:text-blue-700 cursor-pointer">
                    <span>Next</span>
                    <i className="ri-arrow-right-line"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-12 text-gray-800">Popular Resources</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow cursor-pointer">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-play-circle-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold mb-3">Video Tutorials</h3>
              <p className="text-gray-600 mb-4">Step-by-step video guides for all features</p>
              <button className="text-blue-600 font-medium hover:text-blue-700 cursor-pointer">
                Watch Now <i className="ri-arrow-right-line ml-1"></i>
              </button>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow cursor-pointer">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-download-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold mb-3">SDK Downloads</h3>
              <p className="text-gray-600 mb-4">Official SDKs for popular programming languages</p>
              <button className="text-blue-600 font-medium hover:text-blue-700 cursor-pointer">
                Download <i className="ri-arrow-right-line ml-1"></i>
              </button>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow cursor-pointer">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-question-answer-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold mb-3">FAQ</h3>
              <p className="text-gray-600 mb-4">Answers to frequently asked questions</p>
              <button className="text-blue-600 font-medium hover:text-blue-700 cursor-pointer">
                Browse FAQ <i className="ri-arrow-right-line ml-1"></i>
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DocsPage;
